import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { HubNotification, HubNotificationStatus } from '@core/interfaces/notification';
import { AccessRequestStoreService } from '@core/services/access-request/access-request-store.service';
import { HubDestroyService } from '@core/services/hub-destroy/hub-destroy.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { ToastsService } from '@core/services/toasts/toasts.service';
import {
  Observable,
  catchError,
  forkJoin,
  of,
  switchMap,
  takeUntil,
  throwError,
  timer,
} from 'rxjs';
import { HubButtonComponent } from '@shared/atoms/hub-button/hub-button.component';
import { NgbDropdownModule, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';

const NOTIFICATIONS_POLL_INTERVAL_MS = 60_000;
const ACCESS_REQUEST_NOTIFICATION_TYPE = 'ACCESS_REQUEST';

@Component({
  selector: 'hub-main-header-notifications',
  templateUrl: './main-header-notifications.component.html',
  styleUrl: './main-header-notifications.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HubDestroyService],
  imports: [CommonModule, HubButtonComponent, NgbDropdownModule, NgbNavModule],
})
export class MainHeaderNotificationsComponent implements OnInit {
  private notificationService = inject(NotificationsService);
  private hubDestroyService = inject(HubDestroyService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private toastsService = inject(ToastsService);
  private readonly router = inject(Router);
  private readonly accessRequestStore = inject(AccessRequestStoreService);

  isUnreadNotification = false;
  notifications: HubNotification[] = [];
  notificationsToShow: HubNotification[] = [];
  notificationStatus = HubNotificationStatus;
  showUnread = false;
  isProgressMarkOneId = '';
  isProgressMarkAllAsRead = false;

  private readonly seenAccessRequestNotificationIds = new Set<string>();
  private initialized = false;

  get amountOfUnreadNotifications(): number {
    return this.notifications.filter(
      (notification) => notification.status === HubNotificationStatus.UNREAD,
    ).length;
  }

  ngOnInit(): void {
    this.getHubNotifications();
    // Conventions-compliant refresh so decisions reach the bell without a reload.
    timer(NOTIFICATIONS_POLL_INTERVAL_MS, NOTIFICATIONS_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.fetchNotifications()),
        this.hubDestroyService.takeUntilDestroy(),
      )
      .subscribe((notifications) => this.applyNotifications(notifications));
  }

  getHubNotifications() {
    this.fetchNotifications()
      .pipe(this.hubDestroyService.takeUntilDestroy())
      .subscribe((notifications) => this.applyNotifications(notifications));
  }

  onNotificationClick(notification: HubNotification): void {
    this.markAsRead(notification);
    if (notification.link) {
      void this.router.navigateByUrl(notification.link);
    }
  }

  private fetchNotifications(): Observable<HubNotification[]> {
    return this.notificationService.all().pipe(catchError(() => of<HubNotification[]>([])));
  }

  private applyNotifications(notifications: HubNotification[]): void {
    this.notifications = notifications;
    this.isUnreadNotification = notifications.some(
      (notification) => notification.status == HubNotificationStatus.UNREAD,
    );
    this.refreshAccessRequestsOnNewNotifications(notifications);
    this.updateNotificationsToShow();
    this.changeDetectorRef.detectChanges();
  }

  private refreshAccessRequestsOnNewNotifications(notifications: HubNotification[]): void {
    const accessRequestIds = notifications
      .filter((notification) => notification.type === ACCESS_REQUEST_NOTIFICATION_TYPE)
      .map((notification) => notification.id);
    const hasNew = accessRequestIds.some((id) => !this.seenAccessRequestNotificationIds.has(id));
    accessRequestIds.forEach((id) => this.seenAccessRequestNotificationIds.add(id));

    if (this.initialized && hasNew) {
      this.accessRequestStore.refresh();
    }
    this.initialized = true;
  }

  updateNotificationsToShow(): void {
    this.notificationsToShow = this.showUnread
      ? this.notifications.filter(
          (notification) => notification.status == HubNotificationStatus.UNREAD,
        )
      : this.notifications;
  }

  markAsRead(notification: HubNotification) {
    if (notification.status == HubNotificationStatus.UNREAD) {
      this.isProgressMarkOneId = notification.id;
      notification.status = HubNotificationStatus.READ;
      this.notificationService
        .markAsRead(notification.id)
        .pipe(
          catchError((err) => {
            notification.status = HubNotificationStatus.UNREAD;
            this.isProgressMarkOneId = '';
            this.toastsService.show(err.error.detailedMessage);
            this.updateNotificationsToShow();
            this.changeDetectorRef.detectChanges();
            return throwError(() => err);
          }),
          takeUntil(this.hubDestroyService.destroy$),
        )
        .subscribe(() => {
          this.isProgressMarkOneId = '';
          this.updateNotificationsToShow();
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  markAllAsRead() {
    const unreadNotifications = this.notifications.filter(
      (notification) => notification.status == HubNotificationStatus.UNREAD,
    );

    if (unreadNotifications.length > 0) {
      this.isProgressMarkAllAsRead = true;
      const request$ = unreadNotifications.map((notification) =>
        this.notificationService.markAsRead(notification.id),
      );

      forkJoin(request$)
        .pipe(
          catchError((err) => {
            const unreadIds = unreadNotifications.map((notification) => notification.id);
            this.notifications = this.notifications.map((notification) =>
              unreadIds.includes(notification.id)
                ? { ...notification, status: HubNotificationStatus.UNREAD }
                : notification,
            );
            this.isProgressMarkAllAsRead = false;
            this.toastsService.show(err.error.detailedMessage);
            this.updateNotificationsToShow();
            this.changeDetectorRef.detectChanges();
            return throwError(() => err);
          }),
          takeUntil(this.hubDestroyService.destroy$),
        )
        .subscribe(() => {
          this.notifications = this.notifications.map((notification) => ({
            ...notification,
            status: HubNotificationStatus.READ,
          }));
          this.isProgressMarkAllAsRead = false;
          this.updateNotificationsToShow();
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  timeAgo(timestamp: string | Date): string {
    const now = new Date().getTime();
    const createdDate = new Date(timestamp).getTime();
    const timeDifference = now - createdDate;
    const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
    const daysDifference = Math.floor(hoursDifference / 24);

    if (hoursDifference < 24) {
      return hoursDifference <= 1 ? '1 hour ago' : hoursDifference + ' hours ago';
    } else {
      return daysDifference <= 1 ? '1 day ago' : daysDifference + ' days ago';
    }
  }
}
